import { useEffect, useRef } from 'react'
import { MAIN_CONTENT_ID, PAGE_HEADING_ID } from '~/hooks/useRouteFocusAndTitle'

/** Marks each stacked banner slot inside the banner container. Only the visible slot isn't `inert`. */
export const BANNER_SLOT_ATTRIBUTE = 'data-banner-slot'

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

const isUsable = (element: Element) =>
  element.isConnected && !element.closest('[inert]')

const findBannerControl = (container: HTMLElement) =>
  container
    .querySelector(`[${BANNER_SLOT_ATTRIBUTE}]:not([inert])`)
    ?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR) ?? null

const findPageTarget = () =>
  document.getElementById(PAGE_HEADING_ID) ??
  document.getElementById(MAIN_CONTENT_ID)

/** Focus is still on `lost`, or on nothing usable. */
const isFocusLost = (lost: Element) => {
  const active = document.activeElement
  return (
    !active || active === document.body || active === lost || !isUsable(active)
  )
}

/**
 * A slot that was just shown can compute as `visibility: hidden` for a frame
 * (banners transition `all`, which includes the inherited visibility), and
 * `focus()` is ignored until it's shown.
 */
const FOCUS_RETRY_FRAMES = 20

/**
 * Banners hide by unmounting or by their slot turning `inert`, and either one
 * drops focus to `<body>`. When the focused banner control goes away, this
 * moves focus to the next visible banner's first control, or to the page
 * heading when no banner is left.
 *
 * @returns a ref for the element that contains every banner slot.
 */
export const useKeepFocusInBanners = () => {
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    let focusedControl: HTMLElement | null = null
    let retryFrame = 0

    const focusOnceShown = (
      target: HTMLElement,
      lost: HTMLElement,
      framesLeft: number,
    ) => {
      target.focus({ preventScroll: true })
      if (document.activeElement === target) return
      if (framesLeft === 0) {
        findPageTarget()?.focus({ preventScroll: true })
        return
      }
      retryFrame = requestAnimationFrame(() => {
        if (isFocusLost(lost) && isUsable(target)) {
          focusOnceShown(target, lost, framesLeft - 1)
        }
      })
    }

    const handleFocusIn = (event: FocusEvent) => {
      focusedControl = event.target as HTMLElement
    }
    // A blur without a new target is either the control being removed or
    // turning inert, or focus dropping to <body> (e.g. a click on empty page
    // space). Only the first case should move focus later.
    const handleFocusOut = (event: FocusEvent) => {
      const target = event.target as HTMLElement
      const next = event.relatedTarget as Node | null
      if (next) {
        if (!container.contains(next)) focusedControl = null
        return
      }
      queueMicrotask(() => {
        if (focusedControl === target && isUsable(target)) {
          focusedControl = null
        }
      })
    }

    const restoreFocus = () => {
      const lost = focusedControl
      if (!lost || isUsable(lost)) return
      focusedControl = null
      if (!isFocusLost(lost)) return
      cancelAnimationFrame(retryFrame)
      const target = findBannerControl(container) ?? findPageTarget()
      if (target) focusOnceShown(target, lost, FOCUS_RETRY_FRAMES)
    }

    const observer = new MutationObserver(restoreFocus)
    observer.observe(container, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['inert'],
    })
    container.addEventListener('focusin', handleFocusIn)
    container.addEventListener('focusout', handleFocusOut)
    return () => {
      cancelAnimationFrame(retryFrame)
      observer.disconnect()
      container.removeEventListener('focusin', handleFocusIn)
      container.removeEventListener('focusout', handleFocusOut)
    }
  }, [])

  return containerRef
}
